import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Search, ShieldCheck, Wrench, Calendar, Loader2, PackageSearch } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSite } from "@/context/SiteContext";
import { PageHero } from "@/components/Bits";

export default function EquipmentLookup() {
  const { serial } = useParams();
  const navigate = useNavigate();
  const { openQuote } = useSite();
  const [q, setQ] = useState(serial || "");
  const [eq, setEq] = useState(null);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (!serial) { setEq(null); setStatus("idle"); return; }
    setStatus("loading");
    api.get(`/equipment/${encodeURIComponent(serial)}`)
      .then((r) => { setEq(r.data); setStatus("found"); })
      .catch(() => { setEq(null); setStatus("notfound"); });
  }, [serial]);

  const search = (e) => { e.preventDefault(); if (q.trim()) navigate(`/equipamento/${encodeURIComponent(q.trim())}`); };

  return (
    <div>
      <PageHero eyebrow="Pós-venda" title="Consulta de equipamento"
        subtitle="Informe o número de série (ou escaneie o QR Code no equipamento) para ver garantia, histórico de manutenção e solicitar assistência." />
      <div className="mx-auto max-w-3xl px-4 py-16 lg:px-8">
        <form onSubmit={search} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-[#A1A1AA]" />
            <Input data-testid="equipment-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ex.: GR-2026-0001" className="rounded-none border-[#27272A] bg-[#0A0A0B] pl-9" />
          </div>
          <Button type="submit" data-testid="equipment-search-button" className="rounded-none bg-[#FF5500] font-bold hover:bg-[#CC4400]">Consultar</Button>
        </form>

        {status === "loading" && <div className="mt-10 flex items-center gap-2 text-[#A1A1AA]"><Loader2 className="h-4 w-4 animate-spin" /> Buscando...</div>}

        {status === "notfound" && (
          <div className="mt-10 border border-[#27272A] bg-[#151618] p-8 text-center" data-testid="equipment-notfound">
            <PackageSearch className="mx-auto h-10 w-10 text-[#71717A]" />
            <p className="mt-3 text-[#A1A1AA]">Nenhum equipamento encontrado com esse número de série.</p>
          </div>
        )}

        {status === "found" && eq && (
          <div className="mt-10 space-y-6" data-testid="equipment-detail">
            <div className="border border-[#27272A] bg-[#151618] p-8">
              <span className="label-eyebrow text-[#FF5500]">Nº de série</span>
              <h2 className="font-heading text-2xl font-bold text-white">{eq.serial}</h2>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Info label="Modelo" value={eq.model} />
                <Info label="Cliente" value={eq.client_name} />
                <Info label="Cidade" value={eq.client_city} />
                <Info label="Instalação" value={eq.install_date} icon={Calendar} />
                <Info label="Garantia até" value={eq.warranty_until} icon={ShieldCheck} />
              </div>
              {eq.notes && <p className="mt-4 text-sm text-[#A1A1AA]">{eq.notes}</p>}
            </div>

            <div className="border border-[#27272A] bg-[#151618] p-8">
              <h3 className="font-heading mb-4 flex items-center gap-2 text-lg font-bold text-white"><Wrench className="h-5 w-5 text-[#007AFF]" /> Histórico de manutenção</h3>
              {(eq.maintenance_history || []).length === 0 ? (
                <p className="text-sm text-[#A1A1AA]">Sem registros de manutenção.</p>
              ) : (
                <ul className="space-y-3">
                  {eq.maintenance_history.map((m, i) => (
                    <li key={i} className="border-l-2 border-[#FF5500] pl-4">
                      <div className="text-sm font-bold text-white">{m.date}</div>
                      <div className="text-sm text-[#A1A1AA]">{m.description}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <Button data-testid="equipment-assist-button" onClick={() => openQuote({ equipment_type: "Assistência Técnica", product_slug: eq.serial, source: "pos-venda" })}
              className="rounded-none bg-[#FF5500] px-8 py-6 text-base font-bold hover:bg-[#CC4400]">Solicitar Assistência</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="label-eyebrow flex items-center gap-1">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</div>
      <div className="mt-1 text-white">{value || "—"}</div>
    </div>
  );
}
